'use client';

import { ReactNode, Suspense, useCallback, useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

const MAX_LOADER_DURATION_MS = 15000;

const UrlChangeListener = ({ onChange }: { onChange: () => void }) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    onChange();
  }, [pathname, searchParams, onChange]);

  return null;
};

type NavigationLoaderProps = {
  loader: ReactNode;
  pathPrefixes: string[];
  children: ReactNode;
};

const NavigationLoader = ({ loader, pathPrefixes, children }: NavigationLoaderProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const hideLoader = useCallback(() => setIsLoading(false), []);

  useEffect(() => {
    const showLoader = (href: string) => {
      const url = new URL(href, window.location.href);
      const isSameUrl =
        url.pathname === window.location.pathname && url.search === window.location.search;

      if (url.origin !== window.location.origin || isSameUrl) return;
      if (!pathPrefixes.some((prefix) => url.pathname.startsWith(prefix))) return;

      setIsLoading(true);
      window.scrollTo(0, 0);
    };

    const handleClick = (event: MouseEvent) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;

      if (!(anchor instanceof HTMLAnchorElement)) return;
      if ((anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) return;

      showLoader(anchor.href);
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) hideLoader();
    };

    document.addEventListener('click', handleClick);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('click', handleClick);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [pathPrefixes, hideLoader]);

  useEffect(() => {
    if (!isLoading) return undefined;

    const timeoutId = window.setTimeout(hideLoader, MAX_LOADER_DURATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isLoading, hideLoader]);

  return (
    <>
      <Suspense fallback={null}>
        <UrlChangeListener onChange={hideLoader} />
      </Suspense>
      {isLoading && loader}
      <div className={isLoading ? 'hidden' : 'contents'}>{children}</div>
    </>
  );
};

export default NavigationLoader;
